import _isEmpty from 'lodash/isEmpty';
import _pick from 'lodash/pick';
import { format, endOfDay, parse, parseISO } from 'date-fns';
import { makeEntityScaffold } from '@classpass/saga-tools';
import { FULL_ISO_DATE_FORMAT } from 'constants/logic';
import { transformKeysToCamelCase, transformKeysToSnakeCase } from 'helpers/core';
import URLS from 'constants/urls';
import { VENUE_FEATURE_PROBE_HTTP_CONFIG } from 'helpers/session-auth-http';

const FORMAT_DAY = 'yyyy-MM-dd';
const NOT_SPECIFIED_KEY = 'not-specified';
const SCHEDULE_ALLOWED_ATTRIBUTES = ['classId', 'instructorId', 'status'];

export const prepareCreateSchedulePayload = schedule => {
  const {
    id,
    capacity,
    startDate,
    recurrenceEndDate,
    recurrence,
    isNoEndDate,
    scheduleType,
    startAndDuration,
    singleDayExceptions,
    ...restSchedule
  } = schedule;

  const scheduleCapacity = capacity === NOT_SPECIFIED_KEY ? null : capacity;
  const startDateTime = parseISO(startDate);
  const firstStartAndDuration = startAndDuration && startAndDuration[0];
  const isRecurring = scheduleType === 'recurring';
  const allowedSchedulePayload = _pick({ ...restSchedule }, SCHEDULE_ALLOWED_ATTRIBUTES);
  const { durationInMinutes } = firstStartAndDuration;
  const newStartAndDuration = startAndDuration.map(({ time }) => ({
    startDate: format(parse(time, 'HH:mm', startDateTime), FULL_ISO_DATE_FORMAT),
    durationInMinutes,
  }));

  const payload = {
    ...allowedSchedulePayload,
    capacity: scheduleCapacity,
    recurring: isRecurring,
    startAndDuration: newStartAndDuration,
  };

  if (isRecurring) {
    const recurrenceEndDateTime = parseISO(recurrenceEndDate);
    payload.recurrenceEndDate = isNoEndDate ? null : format(endOfDay(recurrenceEndDateTime), FULL_ISO_DATE_FORMAT);
    payload.recurrence = !recurrence ? {} : { days: Object.keys(recurrence).filter(key => recurrence[key] === true) };
    if (!_isEmpty(singleDayExceptions)) {
      payload.recurrence.exclude = singleDayExceptions.map(day => format(parseISO(day), FORMAT_DAY));
    }
  }

  return transformKeysToSnakeCase(payload);
};

export const prepareEditSchedulePayload = schedule => {
  const {
    id,
    capacity,
    startDate,
    recurrenceEndDate,
    recurrence,
    isNoEndDate,
    scheduleType,
    startAndDuration,
    durationInMinutes,
    startTime,
    singleDayExceptions,
    ...restSchedule
  } = schedule;

  const scheduleCapacity = capacity === NOT_SPECIFIED_KEY ? null : capacity;
  const startDateTime = parseISO(startDate);
  const isRecurring = scheduleType === 'recurring';
  const allowedSchedulePayload = _pick({ ...restSchedule }, SCHEDULE_ALLOWED_ATTRIBUTES);

  const payload = {
    ...allowedSchedulePayload,
    capacity: scheduleCapacity,
    recurring: isRecurring,
    startDate: format(parse(startTime, 'HH:mm', startDateTime), FULL_ISO_DATE_FORMAT),
    durationInMinutes,
  };

  if (isRecurring) {
    const recurrenceEndDateTime = parseISO(recurrenceEndDate);
    payload.recurrenceEndDate = isNoEndDate ? null : format(endOfDay(recurrenceEndDateTime), FULL_ISO_DATE_FORMAT);
    payload.recurrence = !recurrence ? {} : { days: Object.keys(recurrence).filter(key => recurrence[key] === true) };
    if (!_isEmpty(singleDayExceptions)) {
      payload.recurrence.exclude = singleDayExceptions.map(day => format(parseISO(day), FORMAT_DAY));
    }
  }

  return transformKeysToSnakeCase(payload);
};

const post = (httpClient, venueId, schedule) => {
  const endpointUrl = URLS.msSchedules.create(venueId);

  return httpClient
    .post(endpointUrl, prepareCreateSchedulePayload(schedule), VENUE_FEATURE_PROBE_HTTP_CONFIG)
    .then(response => transformKeysToCamelCase(response.data));
};

const put = (httpClient, venueId, id, schedule) => {
  const endpointUrl = URLS.msSchedules.update(venueId, id);

  return httpClient
    .put(endpointUrl, prepareEditSchedulePayload(schedule), VENUE_FEATURE_PROBE_HTTP_CONFIG)
    .then(response => transformKeysToCamelCase(response.data));
};

const remove = (httpClient, venueId, scheduleId) => {
  const endpointUrl = URLS.msSchedules.delete(venueId, scheduleId);
  return httpClient
    .delete(endpointUrl, VENUE_FEATURE_PROBE_HTTP_CONFIG)
    .then(response => transformKeysToCamelCase(response.data));
};

const handler = ({ httpClient }, venueId, schedule, isRemove) => {
  const { id } = schedule;

  if (id) {
    if (isRemove) return remove(httpClient, venueId, id);

    return put(httpClient, venueId, id, { ...schedule });
  }

  return post(httpClient, venueId, { ...schedule });
};

export default makeEntityScaffold('SAGA.API.FETCH_SCHEDULES', handler, { defaultKey: 2 });
